import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetPaymentMethodsQuery, useUpdatePaymentMethodMutation } from '../../../store/api/paymentsApi';

type PaymentMethodName = 'Syriatel' | 'Sham' | 'Payeer' | 'Cryptomus' | 'OxaPay' | 'Binance' | 'CoinEx';

interface PaymentMethodDialogProps {
  isOpen: boolean;
  onClose: () => void;
  method: PaymentMethodName;
  currentData?: {
    walletNumber?: string;
    walletNumbers?: string[];
    merchantId?: string;
    walletAddress?: string;
    adminEmail?: string;
    coin?: string;
  };
  onSave: (data: {
    walletNumber?: string;
    walletNumbers?: string[];
    merchantId?: string;
    apiKey?: string;
    secretKey?: string;
    walletAddress?: string;
    accessId?: string;
    adminEmail?: string;
    coin?: string;
  }) => Promise<void>;
  isLoading?: boolean;
}

function PaymentMethodDialog({
  isOpen,
  onClose,
  method,
  currentData,
  onSave,
  isLoading = false,
}: PaymentMethodDialogProps) {
  const { t } = useTranslation();

  // Form states
  const [walletNumber, setWalletNumber] = useState<string>('');
  const [walletNumbers, setWalletNumbers] = useState<string[]>(['']);
  const [merchantId, setMerchantId] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [accessId, setAccessId] = useState<string>('');
  const [adminEmail, setAdminEmail] = useState<string>('');
  const [coin, setCoin] = useState<string>('USDT');

  // Initialize form when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (method === 'Syriatel') {
        setWalletNumbers(
          currentData?.walletNumbers && currentData.walletNumbers.length > 0
            ? [...currentData.walletNumbers]
            : ['']
        );
      } else if (method === 'Sham') {
        setWalletNumber(currentData?.walletNumber || '');
      } else if (method === 'Payeer' || method === 'Cryptomus' || method === 'OxaPay') {
        setMerchantId(currentData?.merchantId || '');
        setApiKey('');
        setSecretKey('');
      } else if (method === 'Binance') {
        setWalletAddress(currentData?.walletAddress || '');
        setApiKey('');
        setSecretKey('');
      } else if (method === 'CoinEx') {
        setAdminEmail(currentData?.adminEmail || '');
        setCoin(currentData?.coin || 'USDT');
        setAccessId('');
        setSecretKey('');
      }
    } else {
      // Reset form when dialog closes
      setWalletNumber('');
      setWalletNumbers(['']);
      setMerchantId('');
      setApiKey('');
      setSecretKey('');
      setWalletAddress('');
      setAccessId('');
      setAdminEmail('');
      setCoin('USDT');
    }
  }, [isOpen, method, currentData]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (method === 'Syriatel') {
      const validWallets = walletNumbers.filter((w) => w.trim() !== '');
      if (validWallets.length === 0) {
        toast.error(t('admin.settings.paymentMethods.error.walletRequired') || 'At least one wallet number is required');
        return;
      }
      await onSave({ walletNumbers: validWallets });
    } else if (method === 'Sham') {
      if (!walletNumber.trim()) {
        toast.error(t('admin.settings.paymentMethods.error.walletRequired') || 'Wallet number is required');
        return;
      }
      await onSave({ walletNumber: walletNumber.trim() });
    } else if (method === 'Payeer' || method === 'Cryptomus' || method === 'OxaPay') {
      if (!merchantId.trim() || !apiKey.trim()) {
        toast.error(
          t('admin.settings.paymentMethods.error.credentialsRequired') || 'Merchant ID and API Key are required'
        );
        return;
      }
      await onSave({ merchantId: merchantId.trim(), apiKey: apiKey.trim() });
    } else if (method === 'Binance') {
      if (!apiKey.trim() || !secretKey.trim() || !walletAddress.trim()) {
        toast.error(
          t('admin.settings.paymentMethods.error.binanceRequired') || 'API Key, Secret Key, and Wallet Address are required'
        );
        return;
      }
      await onSave({ 
        apiKey: apiKey.trim(), 
        secretKey: secretKey.trim(), 
        walletAddress: walletAddress.trim() 
      });
    } else if (method === 'CoinEx') {
      if (!accessId.trim() || !secretKey.trim() || !adminEmail.trim()) {
        toast.error(
          t('admin.settings.paymentMethods.error.coinexRequired') || 'Access ID, Secret Key, and Admin Email are required'
        );
        return;
      }
      await onSave({ 
        accessId: accessId.trim(), 
        secretKey: secretKey.trim(), 
        adminEmail: adminEmail.trim(),
        coin: coin.trim() || 'USDT'
      });
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const addWallet = () => {
    setWalletNumbers([...walletNumbers, '']);
  };

  const removeWallet = (index: number) => {
    setWalletNumbers(walletNumbers.filter((_, i) => i !== index));
  };

  const updateWallet = (index: number, value: string) => {
    const updated = [...walletNumbers];
    updated[index] = value;
    setWalletNumbers(updated);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-neutral-dark/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6 md:p-8 max-w-md w-full animate-scale-in max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-2xl font-heading font-bold text-brand-accent mb-6 text-center">
          {method === 'Syriatel' && (t('admin.settings.paymentMethods.syriatel.title') || 'SyriaTel Cash')}
          {method === 'Sham' && (t('admin.settings.paymentMethods.sham.title') || 'Sham Cash')}
          {method === 'Payeer' && (t('admin.settings.paymentMethods.payeer.title') || 'Payeer')}
          {method === 'Cryptomus' && (t('admin.settings.paymentMethods.cryptomus.title') || 'Cryptomus')}
          {method === 'OxaPay' && (t('admin.settings.paymentMethods.oxapay.title') || 'OxaPay')}
          {method === 'Binance' && (t('admin.settings.paymentMethods.binance.title') || 'Binance')}
          {method === 'CoinEx' && (t('admin.settings.paymentMethods.coinex.title') || 'CoinEx')}
        </h3>

        <div className="space-y-4 mb-6">
          {/* Syriatel - Multiple Wallets */}
          {method === 'Syriatel' && (
            <div>
              <label className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.settings.paymentMethods.syriatel.walletNumbers') || 'Wallet Numbers'}
              </label>
              {walletNumbers.map((wallet, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={wallet}
                    onChange={(e) => updateWallet(index, e.target.value)}
                    placeholder={t('admin.settings.paymentMethods.syriatel.walletNumberPlaceholder') || 'Enter wallet number'}
                    className="flex-1 px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                    disabled={isLoading}
                  />
                  {walletNumbers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeWallet(index)}
                      disabled={isLoading}
                      className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('admin.settings.paymentMethods.syriatel.removeWallet') || 'Remove'}
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addWallet}
                disabled={isLoading}
                className="mt-2 px-4 py-2 bg-neutral-medium text-neutral-light rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {t('admin.settings.paymentMethods.syriatel.addWallet') || 'Add Wallet'}
              </button>
            </div>
          )}

          {/* Sham - Single Wallet */}
          {method === 'Sham' && (
            <div>
              <label className="block text-sm font-medium text-neutral-light mb-2">
                {t('admin.settings.paymentMethods.sham.walletNumber') || 'Wallet Number'}
              </label>
              <input
                type="text"
                value={walletNumber}
                onChange={(e) => setWalletNumber(e.target.value)}
                placeholder={t('admin.settings.paymentMethods.sham.walletNumberPlaceholder') || 'Enter wallet number'}
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                disabled={isLoading}
              />
            </div>
          )}

          {/* Payeer, Cryptomus & OxaPay - Merchant ID & API Key */}
          {(method === 'Payeer' || method === 'Cryptomus' || method === 'OxaPay') && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t(`admin.settings.paymentMethods.${method.toLowerCase()}.merchantId`) || 'Merchant ID'}
                </label>
                <input
                  type="text"
                  value={merchantId}
                  onChange={(e) => setMerchantId(e.target.value)}
                  placeholder={t(`admin.settings.paymentMethods.${method.toLowerCase()}.merchantIdPlaceholder`) || 'Enter merchant ID'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t(`admin.settings.paymentMethods.${method.toLowerCase()}.apiKey`) || 'API Key'}
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={t(`admin.settings.paymentMethods.${method.toLowerCase()}.apiKeyPlaceholder`) || 'Enter API key'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {/* Binance - API Key, Secret Key & Wallet Address */}
          {method === 'Binance' && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.settings.paymentMethods.binance.apiKey') || 'API Key'}
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={t('admin.settings.paymentMethods.binance.apiKeyPlaceholder') || 'Enter Binance API key'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.settings.paymentMethods.binance.secretKey') || 'Secret Key'}
                </label>
                <input
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={t('admin.settings.paymentMethods.binance.secretKeyPlaceholder') || 'Enter Binance secret key'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.settings.paymentMethods.binance.walletAddress') || 'Wallet Address'}
                </label>
                <input
                  type="text"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder={t('admin.settings.paymentMethods.binance.walletAddressPlaceholder') || 'Enter wallet address for receiving USDT'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {/* CoinEx - Access ID, Secret Key, Admin Email & Coin */}
          {method === 'CoinEx' && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.settings.paymentMethods.coinex.accessId') || 'Access ID'}
                </label>
                <input
                  type="text"
                  value={accessId}
                  onChange={(e) => setAccessId(e.target.value)}
                  placeholder={t('admin.settings.paymentMethods.coinex.accessIdPlaceholder') || 'Enter CoinEx Access ID'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.settings.paymentMethods.coinex.secretKey') || 'Secret Key'}
                </label>
                <input
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={t('admin.settings.paymentMethods.coinex.secretKeyPlaceholder') || 'Enter CoinEx Secret Key'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.settings.paymentMethods.coinex.adminEmail') || 'Admin Email'}
                </label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder={t('admin.settings.paymentMethods.coinex.adminEmailPlaceholder') || 'Enter admin email address'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.settings.paymentMethods.coinex.coin') || 'Coin Type'}
                </label>
                <input
                  type="text"
                  value={coin}
                  onChange={(e) => setCoin(e.target.value)}
                  placeholder={t('admin.settings.paymentMethods.coinex.coinPlaceholder') || 'Enter coin type (default: USDT)'}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            onClick={() => {
              void handleSave();
            }}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? t('common.loading') || 'Loading...' : t('admin.settings.paymentMethods.save') || 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PaymentMethodsPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetPaymentMethodsQuery();
  const [updatePaymentMethod, { isLoading: isUpdating }] = useUpdatePaymentMethodMutation();

  const paymentMethods = data?.data?.paymentMethods;

  // Dialog states
  const [openDialog, setOpenDialog] = useState<PaymentMethodName | null>(null);

  const handleSave = async (
    method: PaymentMethodName,
    data: {
      walletNumber?: string;
      walletNumbers?: string[];
      merchantId?: string;
      apiKey?: string;
      secretKey?: string;
      walletAddress?: string;
      accessId?: string;
      adminEmail?: string;
      coin?: string;
    }
  ) => {
    try {
      await updatePaymentMethod({ method, data }).unwrap();
      toast.success(t('admin.settings.paymentMethods.success') || 'Payment method updated successfully');
      setOpenDialog(null);
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update payment method');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.settings.paymentMethods.error.title') || 'Error loading payment methods'}
        </p>
      </div>
    );
  }

  const PaymentMethodCard = ({
    title,
    method,
    configured,
    children,
    delay = 0,
  }: {
    title: string;
    method: PaymentMethodName;
    configured: boolean;
    children: React.ReactNode;
    delay?: number;
  }) => (
    <div
      className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
      style={{
        animation: `slideUp 0.5s ease-out ${delay}s both`,
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent">{title}</h2>
        <span
          className={`px-3 py-1 text-xs font-semibold rounded border ${
            configured
              ? 'bg-green-500/20 text-green-400 border-green-500/50'
              : 'bg-neutral-medium/20 text-neutral-medium border-neutral-medium/50'
          }`}
        >
          {configured
            ? t('admin.settings.paymentMethods.configured') || 'Configured'
            : t('admin.settings.paymentMethods.notConfigured') || 'Not Configured'}
        </span>
      </div>

      <div className="space-y-3 mb-4">{children}</div>

      <button
        onClick={() => setOpenDialog(method)}
        className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm sm:text-base"
      >
        {configured
          ? t('admin.settings.paymentMethods.edit') || 'Edit'
          : t('admin.settings.paymentMethods.add') || 'Add'}
      </button>
    </div>
  );

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header with animation */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.settings.paymentMethods.title') || 'Payment Methods'}
        </h1>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.settings.paymentMethods.subtitle') || 'Manage payment method settings'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Syriatel */}
        <PaymentMethodCard
          title={t('admin.settings.paymentMethods.syriatel.title') || 'SyriaTel Cash'}
          method="Syriatel"
          configured={paymentMethods?.Syriatel.configured || false}
          delay={0.1}
        >
          {paymentMethods?.Syriatel.walletNumbers && paymentMethods.Syriatel.walletNumbers.length > 0 ? (
            <div className="space-y-2">
              {paymentMethods.Syriatel.walletNumbers.map((wallet, index) => (
                <div
                  key={index}
                  className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
                  style={{
                    animation: `fadeIn 0.3s ease-out ${index * 50}ms both`,
                  }}
                >
                  <p className="text-sm text-neutral-light font-mono">{wallet}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-medium text-center py-4">
              {t('admin.settings.paymentMethods.noWallets') || 'No wallets configured'}
            </p>
          )}
        </PaymentMethodCard>

        {/* Sham */}
        <PaymentMethodCard
          title={t('admin.settings.paymentMethods.sham.title') || 'Sham Cash'}
          method="Sham"
          configured={paymentMethods?.Sham.configured || false}
          delay={0.2}
        >
          {paymentMethods?.Sham.walletNumber ? (
            <div
              className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
              style={{
                animation: 'fadeIn 0.3s ease-out both',
              }}
            >
              <p className="text-sm text-neutral-light font-mono">{paymentMethods.Sham.walletNumber}</p>
            </div>
          ) : (
            <p className="text-sm text-neutral-medium text-center py-4">
              {t('admin.settings.paymentMethods.noWallet') || 'No wallet configured'}
            </p>
          )}
        </PaymentMethodCard>

        {/* Payeer */}
        <PaymentMethodCard
          title={t('admin.settings.paymentMethods.payeer.title') || 'Payeer'}
          method="Payeer"
          configured={paymentMethods?.Payeer.configured || false}
          delay={0.3}
        >
          {paymentMethods?.Payeer.merchantId ? (
            <div
              className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
              style={{
                animation: 'fadeIn 0.3s ease-out both',
              }}
            >
              <p className="text-xs text-neutral-medium mb-1">
                {t('admin.settings.paymentMethods.payeer.merchantId') || 'Merchant ID'}
              </p>
              <p className="text-sm text-neutral-light font-mono">{paymentMethods.Payeer.merchantId}</p>
            </div>
          ) : (
            <p className="text-sm text-neutral-medium text-center py-4">
              {t('admin.settings.paymentMethods.notConfigured') || 'Not Configured'}
            </p>
          )}
        </PaymentMethodCard>

        {/* Cryptomus */}
        <PaymentMethodCard
          title={t('admin.settings.paymentMethods.cryptomus.title') || 'Cryptomus'}
          method="Cryptomus"
          configured={paymentMethods?.Cryptomus.configured || false}
          delay={0.4}
        >
          {paymentMethods?.Cryptomus.merchantId ? (
            <div
              className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
              style={{
                animation: 'fadeIn 0.3s ease-out both',
              }}
            >
              <p className="text-xs text-neutral-medium mb-1">
                {t('admin.settings.paymentMethods.cryptomus.merchantId') || 'Merchant ID'}
              </p>
              <p className="text-sm text-neutral-light font-mono">{paymentMethods.Cryptomus.merchantId}</p>
            </div>
          ) : (
            <p className="text-sm text-neutral-medium text-center py-4">
              {t('admin.settings.paymentMethods.notConfigured') || 'Not Configured'}
            </p>
          )}
        </PaymentMethodCard>

        {/* OxaPay */}
        <PaymentMethodCard
          title={t('admin.settings.paymentMethods.oxapay.title') || 'OxaPay'}
          method="OxaPay"
          configured={paymentMethods?.OxaPay?.configured || false}
          delay={0.5}
        >
          {paymentMethods?.OxaPay?.merchantId ? (
            <div
              className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
              style={{
                animation: 'fadeIn 0.3s ease-out both',
              }}
            >
              <p className="text-xs text-neutral-medium mb-1">
                {t('admin.settings.paymentMethods.oxapay.merchantId') || 'Merchant ID'}
              </p>
              <p className="text-sm text-neutral-light font-mono">{paymentMethods.OxaPay.merchantId}</p>
            </div>
          ) : (
            <p className="text-sm text-neutral-medium text-center py-4">
              {t('admin.settings.paymentMethods.notConfigured') || 'Not Configured'}
            </p>
          )}
        </PaymentMethodCard>

        {/* Binance */}
        <PaymentMethodCard
          title={t('admin.settings.paymentMethods.binance.title') || 'Binance'}
          method="Binance"
          configured={paymentMethods?.Binance?.configured || false}
          delay={0.6}
        >
          {paymentMethods?.Binance?.walletAddress ? (
            <div
              className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
              style={{
                animation: 'fadeIn 0.3s ease-out both',
              }}
            >
              <p className="text-xs text-neutral-medium mb-1">
                {t('admin.settings.paymentMethods.binance.walletAddress') || 'Wallet Address'}
              </p>
              <p className="text-sm text-neutral-light font-mono break-all">{paymentMethods.Binance.walletAddress}</p>
            </div>
          ) : (
            <p className="text-sm text-neutral-medium text-center py-4">
              {t('admin.settings.paymentMethods.notConfigured') || 'Not Configured'}
            </p>
          )}
        </PaymentMethodCard>

        {/* CoinEx */}
        <PaymentMethodCard
          title={t('admin.settings.paymentMethods.coinex.title') || 'CoinEx'}
          method="CoinEx"
          configured={paymentMethods?.CoinEx?.configured || false}
          delay={0.7}
        >
          {paymentMethods?.CoinEx?.adminEmail ? (
            <div className="space-y-2">
              <div
                className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
                style={{
                  animation: 'fadeIn 0.3s ease-out both',
                }}
              >
                <p className="text-xs text-neutral-medium mb-1">
                  {t('admin.settings.paymentMethods.coinex.adminEmail') || 'Admin Email'}
                </p>
                <p className="text-sm text-neutral-light font-mono">{paymentMethods.CoinEx.adminEmail}</p>
              </div>
              {paymentMethods.CoinEx.coin && (
                <div
                  className="p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50"
                  style={{
                    animation: 'fadeIn 0.3s ease-out both',
                  }}
                >
                  <p className="text-xs text-neutral-medium mb-1">
                    {t('admin.settings.paymentMethods.coinex.coin') || 'Coin Type'}
                  </p>
                  <p className="text-sm text-neutral-light font-mono">{paymentMethods.CoinEx.coin}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-neutral-medium text-center py-4">
              {t('admin.settings.paymentMethods.notConfigured') || 'Not Configured'}
            </p>
          )}
        </PaymentMethodCard>
      </div>

      {/* Dialogs */}
      {openDialog === 'Syriatel' && (
        <PaymentMethodDialog
          isOpen={openDialog === 'Syriatel'}
          onClose={() => setOpenDialog(null)}
          method="Syriatel"
          currentData={{
            walletNumbers: paymentMethods?.Syriatel.walletNumbers,
          }}
          onSave={async (data) => {
            await handleSave('Syriatel', data);
          }}
          isLoading={isUpdating}
        />
      )}

      {openDialog === 'Sham' && (
        <PaymentMethodDialog
          isOpen={openDialog === 'Sham'}
          onClose={() => setOpenDialog(null)}
          method="Sham"
          currentData={{
            walletNumber: paymentMethods?.Sham.walletNumber,
          }}
          onSave={async (data) => {
            await handleSave('Sham', data);
          }}
          isLoading={isUpdating}
        />
      )}

      {openDialog === 'Payeer' && (
        <PaymentMethodDialog
          isOpen={openDialog === 'Payeer'}
          onClose={() => setOpenDialog(null)}
          method="Payeer"
          currentData={{
            merchantId: paymentMethods?.Payeer.merchantId,
          }}
          onSave={async (data) => {
            await handleSave('Payeer', data);
          }}
          isLoading={isUpdating}
        />
      )}

      {openDialog === 'Cryptomus' && (
        <PaymentMethodDialog
          isOpen={openDialog === 'Cryptomus'}
          onClose={() => setOpenDialog(null)}
          method="Cryptomus"
          currentData={{
            merchantId: paymentMethods?.Cryptomus.merchantId,
          }}
          onSave={async (data) => {
            await handleSave('Cryptomus', data);
          }}
          isLoading={isUpdating}
        />
      )}

      {openDialog === 'OxaPay' && (
        <PaymentMethodDialog
          isOpen={openDialog === 'OxaPay'}
          onClose={() => setOpenDialog(null)}
          method="OxaPay"
          currentData={{
            merchantId: paymentMethods?.OxaPay?.merchantId,
          }}
          onSave={async (data) => {
            await handleSave('OxaPay', data);
          }}
          isLoading={isUpdating}
        />
      )}

      {openDialog === 'Binance' && (
        <PaymentMethodDialog
          isOpen={openDialog === 'Binance'}
          onClose={() => setOpenDialog(null)}
          method="Binance"
          currentData={{
            walletAddress: paymentMethods?.Binance?.walletAddress,
          }}
          onSave={async (data) => {
            await handleSave('Binance', data);
          }}
          isLoading={isUpdating}
        />
      )}

      {openDialog === 'CoinEx' && (
        <PaymentMethodDialog
          isOpen={openDialog === 'CoinEx'}
          onClose={() => setOpenDialog(null)}
          method="CoinEx"
          currentData={{
            adminEmail: paymentMethods?.CoinEx?.adminEmail,
            coin: paymentMethods?.CoinEx?.coin,
          }}
          onSave={async (data) => {
            await handleSave('CoinEx', data);
          }}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
}
